import CategoriesAndThematiquesTab from "@/components/blocks/featured_categories/CategoriesAndThematiquesTab";
import { mergeFrenchImage } from "@/helpers/frenchImage";
import { appLocales, getPrismicLocale, Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { getAllByIDs, getByID } from "@/lib/prismic";
import { Content, isFilled } from "@prismicio/client";
import { SliceComponentProps } from "@prismicio/react";
import { use } from "react";

export type CategoriesAndThematiquesListProps = SliceComponentProps<Content.CategoriesAndThematiquesListSlice> & {
  context?: { locale?: Locales };
};

const CategoriesAndThematiquesList = ({ slice, context }: CategoriesAndThematiquesListProps) => {
  const locale = context?.locale || appLocales.defaultLocale;
  const frLocale = getPrismicLocale("fr");

  const { categories, thematiques } = slice.primary;

  const validCategories = categories.map((item) => item.categorie).filter(isFilled.contentRelationship);
  const validThematiques = thematiques.map((item) => item.thematique).filter(isFilled.contentRelationship);

  const categoriesData = use(
    getAllByIDs(
      validCategories.map((rel) => rel.id),
      { lang: getPrismicLocale(locale) }
    )
  ) as (Content.CategorieDocument & { alternate_languages: { id: string; lang: string }[] })[];

  const thematiquesData = use(
    getAllByIDs(
      validThematiques.map((rel) => rel.id),
      { lang: getPrismicLocale(locale) }
    )
  ) as (Content.InspirationDocument & { alternate_languages: { id: string; lang: string }[] })[];

  const categoriesFr = use(
    Promise.all(
      categoriesData.map(async (doc) => {
        const fr = doc.alternate_languages?.find((alt) => alt.lang === frLocale);
        return fr ? await getByID(fr.id, { lang: frLocale }) : null;
      })
    )
  ) as (Content.CategorieDocument | null)[];

  const thematiquesFr = use(
    Promise.all(
      thematiquesData.map(async (doc) => {
        const fr = doc.alternate_languages?.find((alt) => alt.lang === frLocale);
        return fr ? await getByID(fr.id, { lang: frLocale }) : null;
      })
    )
  ) as (Content.InspirationDocument | null)[];

  const mergedCategories = mergeFrenchImage(categoriesData, categoriesFr, getPrismicLocale(locale), frLocale);
  const mergedThematiques = mergeFrenchImage(thematiquesData, thematiquesFr, getPrismicLocale(locale), frLocale);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      aria-labelledby={__("Featured categories", locale)}
      className="py-7.5"
    >
      <div className="container px-4 md:px-7.5">
        <CategoriesAndThematiquesTab locale={locale} categories={mergedCategories} thematiques={mergedThematiques} />
      </div>
    </section>
  );
};

export default CategoriesAndThematiquesList;
